"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SpotlightOverlay = void 0;
const api_1 = require("@react-google-maps/api");
const react_1 = __importDefault(require("react"));
const client_1 = __importDefault(require("react-dom/client"));
/**
 * Need to use a function because the `google` namespace
 * is not available during module resolution.
 * */
function getSpotlightOverlay() {
    // Docs for custom overlays: https://developers.google.com/maps/documentation/javascript/customoverlays
    class Overlay extends google.maps.OverlayView {
        _radiusKm;
        _center;
        _wrapperDiv;
        _reactRoot;
        constructor(radiusKm, center) {
            super();
            this._radiusKm = radiusKm * (Math.sqrt(2) - 0.007); // dirty hack to correct the spotlight, Google geometry problem probably
            this._center = center;
        }
        onAdd() {
            const wrapperDiv = document.createElement("div");
            this._wrapperDiv = wrapperDiv;
            this._reactRoot = client_1.default.createRoot(wrapperDiv);
            wrapperDiv.style.borderStyle = "none";
            wrapperDiv.style.borderWidth = "0px";
            wrapperDiv.style.position = "absolute";
            wrapperDiv.style.width = "100%";
            wrapperDiv.style.height = "100%";
            wrapperDiv.id = "DIV_WRAPPER";
            const panes = this.getPanes();
            panes.overlayLayer.appendChild(wrapperDiv);
        }
        draw() {
            const containerDimensions = this._getContainerDimensions();
            const circleDimensions = this._getCircleDimensions();
            if (containerDimensions) {
                // Draw the container div. It provides a wrapper to position the SVG
                const { top, height, left, width } = containerDimensions;
                this._wrapperDiv.style.left = left + "px";
                this._wrapperDiv.style.top = top + "px";
                this._wrapperDiv.style.width = width + "px";
                this._wrapperDiv.style.height = height + "px";
            }
            this._reactRoot?.render(react_1.default.createElement("svg", { style: {
                    width: "100%",
                    height: "100%",
                } },
                circleDimensions && (react_1.default.createElement("defs", null,
                    react_1.default.createElement("mask", { id: "mask", width: "100", height: "100" },
                        react_1.default.createElement("rect", { width: "100%", height: "100%", fill: "white" }),
                        react_1.default.createElement("circle", { cx: circleDimensions.cx, cy: circleDimensions.cy, r: circleDimensions.radius })))),
                react_1.default.createElement("rect", { width: "100%", height: "100%", mask: "url(#mask)", fill: "black", fillOpacity: 0.18 })));
        }
        // Need to implement this method (even if empty) to avoid errors when removing the overlay
        onRemove() { }
        /**
         * Returns the dimensions for the overlay container.
         *
         * This spans the entire visible map area, and is used for positioning the SVG
         * coordinate system so that it matches the visible map area.
         * */
        _getContainerDimensions() {
            const overlayProjection = this.getProjection();
            const visibleRegion = overlayProjection.getVisibleRegion();
            if (!visibleRegion) {
                return;
            }
            const sw = overlayProjection.fromLatLngToDivPixel(visibleRegion.latLngBounds.getSouthWest());
            const ne = overlayProjection.fromLatLngToDivPixel(visibleRegion.latLngBounds.getNorthEast());
            const width = ne.x - sw.x;
            const height = sw.y - ne.y;
            return {
                width,
                height,
                left: sw.x,
                top: ne.y,
            };
        }
        /**
         * Returns the dimensions for the transparent circle.
         * */
        _getCircleDimensions() {
            if (this._radiusKm === 0) {
                return;
            }
            const overlayProjection = this.getProjection();
            const visibleRegion = overlayProjection.getVisibleRegion();
            if (!visibleRegion) {
                return;
            }
            const radiusMeters = this._radiusKm * 1000;
            const circleTopLeft = google.maps.geometry.spherical.computeOffset(this._center, radiusMeters, 315);
            const circleTopLeftPixel = overlayProjection.fromLatLngToDivPixel(circleTopLeft);
            const circleCenterPixel = overlayProjection.fromLatLngToDivPixel(this._center);
            const visibleRegionNorthEastPixel = overlayProjection.fromLatLngToDivPixel(visibleRegion.latLngBounds.getNorthEast());
            /**
             * The Google Maps overlay projection has an origin at the center of the map.
             * That is also where `this._center` is located. The SVG coordinate system
             * has an origin in the top left corner (to make it easier to draw the
             * dark overlay in a full width). Given this, when using the projection
             * to resolve the position of `this._center`, we get something close to (0, 0).
             * So we need to offset the coordinates so that we can draw in the actual position.
             * */
            const cx = circleCenterPixel.x + visibleRegionNorthEastPixel.x;
            const cy = circleCenterPixel.y - visibleRegionNorthEastPixel.y;
            const radius = circleCenterPixel.x - circleTopLeftPixel.x;
            return {
                cx,
                cy,
                radius,
            };
        }
    }
    return Overlay;
}
/**
 * Component to render a darkened overlay with a transparent circle at `centerLatitude` and `centerLongitude`.
 * The circle has a radius of `radiusKm` kilometers.
 * */
function SpotlightOverlay({ radiusKm, centerLongitude, centerLatitude }) {
    const map = (0, api_1.useGoogleMap)();
    react_1.default.useEffect(() => {
        if (!map) {
            return;
        }
        const Overlay = getSpotlightOverlay();
        const overlay = new Overlay(radiusKm, {
            lat: centerLatitude,
            lng: centerLongitude,
        });
        overlay.setMap(map);
        return () => {
            overlay.setMap(null);
        };
    }, [map, radiusKm, centerLatitude, centerLongitude]);
    return null;
}
exports.SpotlightOverlay = SpotlightOverlay;
//# sourceMappingURL=SpotlightOverlay.js.map